/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetTipiScadenzaParams,
  TipoScadenzaDTO,
  TipoScadenzaRequest,
  WsProblema,
  WsRispostaPaginataListTipoScadenzaDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ottiene i dettagli di un tipo scadenza
 * @summary Dettaglio Tipo scadenza
 */
export const getTipiScadenzaIdTipoScadenza = (
  idTipoScadenza: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoScadenzaDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-scadenza/${idTipoScadenza}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiScadenzaIdTipoScadenzaQueryKey = (
  idTipoScadenza: string,
) => [`/framework-cfg-procedimenti/tipi-scadenza/${idTipoScadenza}`] as const;

export const getGetTipiScadenzaIdTipoScadenzaQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiScadenzaIdTipoScadenza>>,
  TError = ErrorType<WsProblema>,
>(
  idTipoScadenza: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiScadenzaIdTipoScadenza>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiScadenzaIdTipoScadenza>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTipiScadenzaIdTipoScadenzaQueryKey(idTipoScadenza);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiScadenzaIdTipoScadenza>>
  > = ({ signal }) =>
    getTipiScadenzaIdTipoScadenza(idTipoScadenza, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idTipoScadenza, ...queryOptions };
};

export type GetTipiScadenzaIdTipoScadenzaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiScadenzaIdTipoScadenza>>
>;
export type GetTipiScadenzaIdTipoScadenzaQueryError = ErrorType<WsProblema>;

/**
 * @summary Dettaglio Tipo scadenza
 */
export const useGetTipiScadenzaIdTipoScadenza = <
  TData = Awaited<ReturnType<typeof getTipiScadenzaIdTipoScadenza>>,
  TError = ErrorType<WsProblema>,
>(
  idTipoScadenza: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiScadenzaIdTipoScadenza>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiScadenzaIdTipoScadenzaQueryOptions(
    idTipoScadenza,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna un tipo scadenza
 * @summary Aggiorna un tipo scadenza
 */
export const putTipiScadenzaIdTipoScadenza = (
  idTipoScadenza: string,
  tipoScadenzaRequest: TipoScadenzaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoScadenzaDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-scadenza/${idTipoScadenza}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tipoScadenzaRequest,
    },
    options,
  );
};

export const getPutTipiScadenzaIdTipoScadenzaMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiScadenzaIdTipoScadenza>>,
    TError,
    { idTipoScadenza: string; data: TipoScadenzaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiScadenzaIdTipoScadenza>>,
  TError,
  { idTipoScadenza: string; data: TipoScadenzaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiScadenzaIdTipoScadenza>>,
    { idTipoScadenza: string; data: TipoScadenzaRequest }
  > = props => {
    const { idTipoScadenza, data } = props ?? {};

    return putTipiScadenzaIdTipoScadenza(idTipoScadenza, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiScadenzaIdTipoScadenzaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiScadenzaIdTipoScadenza>>
>;
export type PutTipiScadenzaIdTipoScadenzaMutationBody = TipoScadenzaRequest;
export type PutTipiScadenzaIdTipoScadenzaMutationError = ErrorType<WsProblema>;

/**
 * @summary Aggiorna un tipo scadenza
 */
export const usePutTipiScadenzaIdTipoScadenza = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiScadenzaIdTipoScadenza>>,
    TError,
    { idTipoScadenza: string; data: TipoScadenzaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutTipiScadenzaIdTipoScadenzaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove un tipo scadenza
 * @summary Elimina un tipo scadenza
 */
export const deleteTipiScadenzaIdTipoScadenza = (
  idTipoScadenza: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-procedimenti/tipi-scadenza/${idTipoScadenza}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteTipiScadenzaIdTipoScadenzaMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiScadenzaIdTipoScadenza>>,
    TError,
    { idTipoScadenza: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTipiScadenzaIdTipoScadenza>>,
  TError,
  { idTipoScadenza: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTipiScadenzaIdTipoScadenza>>,
    { idTipoScadenza: string }
  > = props => {
    const { idTipoScadenza } = props ?? {};

    return deleteTipiScadenzaIdTipoScadenza(idTipoScadenza, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTipiScadenzaIdTipoScadenzaMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTipiScadenzaIdTipoScadenza>>
>;

export type DeleteTipiScadenzaIdTipoScadenzaMutationError =
  ErrorType<WsProblema>;

/**
 * @summary Elimina un tipo scadenza
 */
export const useDeleteTipiScadenzaIdTipoScadenza = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiScadenzaIdTipoScadenza>>,
    TError,
    { idTipoScadenza: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteTipiScadenzaIdTipoScadenzaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco dei tipi scadenza ordinati per codice
 * @summary Elenco Tipi Scadenza
 */
export const getTipiScadenza = (
  params?: GetTipiScadenzaParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTipoScadenzaDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-scadenza`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiScadenzaQueryKey = (params?: GetTipiScadenzaParams) =>
  [
    `/framework-cfg-procedimenti/tipi-scadenza`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiScadenzaQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiScadenza>>,
  TError = ErrorType<WsProblema>,
>(
  params?: GetTipiScadenzaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiScadenza>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiScadenza>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiScadenzaQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTipiScadenza>>> = ({
    signal,
  }) => getTipiScadenza(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiScadenzaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiScadenza>>
>;
export type GetTipiScadenzaQueryError = ErrorType<WsProblema>;

/**
 * @summary Elenco Tipi Scadenza
 */
export const useGetTipiScadenza = <
  TData = Awaited<ReturnType<typeof getTipiScadenza>>,
  TError = ErrorType<WsProblema>,
>(
  params?: GetTipiScadenzaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiScadenza>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiScadenzaQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un tipo scadenza
 * @summary Crea un tipo scadenza
 */
export const postTipiScadenza = (
  tipoScadenzaRequest: TipoScadenzaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoScadenzaDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-scadenza`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tipoScadenzaRequest,
    },
    options,
  );
};

export const getPostTipiScadenzaMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiScadenza>>,
    TError,
    { data: TipoScadenzaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipiScadenza>>,
  TError,
  { data: TipoScadenzaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipiScadenza>>,
    { data: TipoScadenzaRequest }
  > = props => {
    const { data } = props ?? {};

    return postTipiScadenza(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipiScadenzaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipiScadenza>>
>;
export type PostTipiScadenzaMutationBody = TipoScadenzaRequest;
export type PostTipiScadenzaMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea un tipo scadenza
 */
export const usePostTipiScadenza = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiScadenza>>,
    TError,
    { data: TipoScadenzaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTipiScadenzaMutationOptions(options);

  return useMutation(mutationOptions);
};
